/**
 * Radial Menu
 *
 * A kind-agnostic, vanilla-DOM popup that arrays buttons radially around
 * a world-space anchor. Built as the radial sibling of the linear
 * `ContextMenu` (../contextMenu/ContextMenu.ts) so tools can adopt either
 * shape against the same lifecycle.
 *
 * Capabilities:
 *   - Show a set of icon buttons fanned around the anchor.
 *   - Morph the content into an inline numeric input + confirm/cancel
 *     buttons (used by the LineTool's edge-op "distance" flow).
 *   - Track the world anchor across camera moves (reprojects each frame).
 *   - Outside-click + Esc dismiss.
 *
 * Adopted by the LineTool's edge-op controller today; structured so the
 * door/window tool, skeleton editor, and future tools can drop it in
 * without changes.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';

export interface RadialMenuItem {
  /** Stable id for testing / shortcuts. */
  id: string;
  /** Inline SVG markup — same convention as `ContextMenuItem.iconSvg`. */
  iconSvg: string;
  /** Hover tooltip text (browser-native `title` attribute). */
  tooltip: string;
  onClick(): void;
  /**
   * When true, the menu auto-hides after the click. Default: false — most
   * items lead the user into a follow-up interaction (pick a target, type
   * a value) and the menu typically morphs in place instead.
   */
  closeOnClick?: boolean;
}

export interface RadialMenuInputContent {
  type: 'input';
  /** Optional caption shown above the input. */
  label?: string;
  /** Pre-filled string in the input field. */
  initialValue: string;
  placeholder?: string;
  /** Suffix shown next to the number ("m", "ft", "°", …). */
  unit?: string;
  /** `<input>` step, in the displayed unit. Defaults to `'0.05'` (the meter snap grid). */
  step?: string;
  /** Fires on Enter or check-button click. The string is parsed to a number by the caller. */
  onConfirm(value: string): void;
  /** Fires on Esc or x-button click. */
  onCancel(): void;
}

export interface RadialMenuItemsContent {
  type: 'items';
  items: RadialMenuItem[];
  /**
   * Fires when the menu is dismissed without picking an item (outside-click
   * or Esc). Lets the owner tear down selection / highlight state — without
   * it, `hide()` removes the menu DOM but the caller never learns it closed.
   */
  onClose?(): void;
}

export interface RadialMenuHintContent {
  type: 'hint';
  /** Short status message anchored to the source handle. */
  message: string;
}

export type RadialMenuContent = RadialMenuItemsContent | RadialMenuInputContent | RadialMenuHintContent;

const BUTTON_SIZE_PX = 44;
/** Icon glyph size inside the bubble — fixed regardless of bubble size. */
const ICON_SIZE_PX = 24;
const RADIUS_PX = 62;
const INPUT_WIDTH_PX = 110;

/** Resting button background / icon color. */
const BUTTON_BG = 'rgba(20, 20, 20, 0.92)';
const BUTTON_COLOR = 'rgba(255, 255, 255, 0.85)';
/**
 * Hover state, matched to the view-switch radio buttons' dark-mode hover
 * (`dark:hover:bg-[#868994]` + `dark:hover:text-neon-green-300`). The
 * neon-green-300 token resolves to #B5FFA1 (see index.css).
 */
const HOVER_BG = '#868994';
const HOVER_COLOR = '#B5FFA1';

/** Inline SVG icons matching the HeroIcons 24-outline conventions of ContextMenu. */
const SVG_BASE = 'fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" xmlns="http://www.w3.org/2000/svg"';

export const ICON_CHECK_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
</svg>`;

export const ICON_XMARK_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
</svg>`;

export class RadialMenu {
  private viewer: Viewer;
  private container: HTMLDivElement;
  private worldAnchor: THREE.Vector3 | null = null;
  private content: RadialMenuContent | null = null;

  private readonly onUpdateBound: () => void;
  private readonly onDocumentClickBound: (e: MouseEvent) => void;
  private readonly onKeyDownBound: (e: KeyboardEvent) => void;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.container = document.createElement('div');
    this.container.style.cssText = `
      position: absolute;
      display: none;
      width: 0;
      height: 0;
      pointer-events: none;
      z-index: 10000;
    `;
    // The container itself is a 0x0 anchor; buttons / input are positioned
    // absolutely against it with their own transforms so they stay
    // pointer-interactive.
    this.viewer.renderArea.appendChild(this.container);

    this.onUpdateBound = (): void => this.reprojectToScreen();
    this.onDocumentClickBound = (e: MouseEvent): void => this.handleDocumentClick(e);
    this.onKeyDownBound = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (this.content?.type === 'input') {
          this.content.onCancel();
        } else if (this.content?.type === 'items') {
          this.content.onClose?.();
          this.hide();
        } else {
          this.hide();
        }
      }
    };
  }

  /** Current world anchor, or null when hidden. */
  get currentAnchor(): THREE.Vector3 | null {
    return this.worldAnchor;
  }

  isVisible(): boolean {
    return this.container.style.display !== 'none';
  }

  /**
   * Show the menu with `content`, anchored at `worldAnchor`. If already
   * visible, content + anchor update in place (no flicker).
   */
  show(content: RadialMenuContent, worldAnchor: THREE.Vector3): void {
    this.worldAnchor = worldAnchor.clone();
    this.setContent(content);
    if (this.container.style.display === 'none') {
      this.container.style.display = 'block';
      this.viewer.addEventListener('update', this.onUpdateBound);
      // Run on next tick so the click that opened the menu doesn't
      // immediately close it.
      setTimeout(() => {
        document.addEventListener('mousedown', this.onDocumentClickBound, true);
      }, 0);
      window.addEventListener('keydown', this.onKeyDownBound);
    }
    this.reprojectToScreen();
  }

  /** Replace the menu's content in place. Anchor and listeners stay attached. */
  setContent(content: RadialMenuContent): void {
    this.content = content;
    if (content.type === 'items') {
      this.renderItems(content.items);
    } else if (content.type === 'input') {
      this.renderInput(content);
    } else {
      this.renderHint(content);
    }
    // Morphing content while shown (buttons → input panel) changes the
    // footprint — re-run the fit-to-frame clamp right away instead of
    // waiting for the next viewer update tick.
    if (this.isVisible() && this.worldAnchor) this.reprojectToScreen();
  }

  hide(): void {
    if (this.container.style.display === 'none') return;
    this.container.style.display = 'none';
    this.worldAnchor = null;
    this.content = null;
    this.container.innerHTML = '';
    this.viewer.removeEventListener('update', this.onUpdateBound);
    document.removeEventListener('mousedown', this.onDocumentClickBound, true);
    window.removeEventListener('keydown', this.onKeyDownBound);
  }

  dispose(): void {
    this.hide();
    this.container.remove();
  }

  // ─── Rendering ──────────────────────────────────────────────────────

  private renderItems(items: RadialMenuItem[]): void {
    this.container.innerHTML = '';

    const n = items.length;
    if (n === 0) return;

    // Fan layout: center the buttons across a 270° arc on the bottom-right
    // quadrant so the menu stays clear of the cursor reading area above.
    // For 3 buttons the spacing is 90°; tweak via `arcDegrees` if other
    // counts feel cramped.
    const arcDegrees = n === 1 ? 0 : Math.min(360, 90 * (n - 1));
    const startDeg = -arcDegrees / 2;
    const stepDeg = n > 1 ? arcDegrees / (n - 1) : 0;

    for (let i = 0; i < n; i++) {
      const item = items[i];
      const angleDeg = startDeg + i * stepDeg;
      const angleRad = (angleDeg * Math.PI) / 180;
      const dx = Math.cos(angleRad) * RADIUS_PX;
      const dy = Math.sin(angleRad) * RADIUS_PX;

      const btn = document.createElement('button');
      btn.title = item.tooltip;
      btn.style.cssText = `
        position: absolute;
        left: 0; top: 0;
        width: ${BUTTON_SIZE_PX}px;
        height: ${BUTTON_SIZE_PX}px;
        transform: translate(${dx - BUTTON_SIZE_PX / 2}px, ${dy - BUTTON_SIZE_PX / 2}px);
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${BUTTON_BG};
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 50%;
        color: ${BUTTON_COLOR};
        cursor: pointer;
        padding: 0;
        pointer-events: auto;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      `;
      // Hover matches the view-switch radio buttons' dark-mode hover: grey
      // background (#868994) with a neon-green-300 (#B5FFA1) icon.
      btn.addEventListener('mouseenter', () => {
        btn.style.background = HOVER_BG;
        btn.style.color = HOVER_COLOR;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = BUTTON_BG;
        btn.style.color = BUTTON_COLOR;
      });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (item.closeOnClick) this.hide();
        item.onClick();
      });

      const iconHost = document.createElement('span');
      iconHost.style.cssText = `width: ${ICON_SIZE_PX}px; height: ${ICON_SIZE_PX}px; display: inline-flex; align-items: center; justify-content: center;`;
      iconHost.innerHTML = item.iconSvg;
      // Icons carry their own width/height attrs; force them to fill the host
      // so they take up more of the bubble regardless of the source glyph size.
      const svg = iconHost.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
      }
      btn.appendChild(iconHost);

      this.container.appendChild(btn);
    }
  }

  private renderInput(content: RadialMenuInputContent): void {
    this.container.innerHTML = '';

    const panel = document.createElement('div');
    panel.style.cssText = `
      position: absolute;
      left: 0; top: 0;
      transform: translate(${RADIUS_PX}px, -${BUTTON_SIZE_PX / 2}px);
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 8px;
      background: rgba(20, 20, 20, 0.92);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      color: rgba(255, 255, 255, 0.9);
      pointer-events: auto;
      font: 13px/1 sans-serif;
    `;

    if (content.label) {
      const label = document.createElement('span');
      label.textContent = content.label;
      label.style.cssText = 'opacity: 0.7; margin-right: 2px;';
      panel.appendChild(label);
    }

    const input = document.createElement('input');
    input.type = 'number';
    input.step = content.step ?? '0.05';
    input.value = content.initialValue;
    if (content.placeholder) input.placeholder = content.placeholder;
    input.style.cssText = `
      width: ${INPUT_WIDTH_PX}px;
      padding: 4px 6px;
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      color: white;
      font: 13px/1 sans-serif;
      outline: none;
    `;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        content.onConfirm(input.value);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        content.onCancel();
      } else {
        // Don't let typed digits propagate to the viewer (could trigger hotkeys).
        e.stopPropagation();
      }
    });
    panel.appendChild(input);

    if (content.unit) {
      const unit = document.createElement('span');
      unit.textContent = content.unit;
      unit.style.cssText = 'opacity: 0.7;';
      panel.appendChild(unit);
    }

    const t = this.viewer.translationService;
    const confirm = this.makeIconButton(ICON_CHECK_SVG, t.t('confirm'), () => content.onConfirm(input.value));
    const cancel = this.makeIconButton(ICON_XMARK_SVG, t.t('cancel'), () => content.onCancel());
    panel.appendChild(confirm);
    panel.appendChild(cancel);

    this.container.appendChild(panel);

    // Focus + select after mounting so the user can immediately type.
    setTimeout(() => {
      input.focus();
      input.select();
    }, 0);
  }

  private makeIconButton(iconSvg: string, tooltip: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.title = tooltip;
    btn.style.cssText = `
      width: 24px;
      height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      padding: 0;
      border-radius: 3px;
    `;
    btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(255, 255, 255, 0.10)'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    const iconHost = document.createElement('span');
    iconHost.style.cssText = 'width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center;';
    iconHost.innerHTML = iconSvg;
    btn.appendChild(iconHost);
    return btn;
  }

  // ─── Positioning ────────────────────────────────────────────────────

  /** Gap (px) kept between the menu content and the render-frame edges. */
  private static readonly EDGE_MARGIN_PX = 4;

  private reprojectToScreen(): void {
    if (!this.worldAnchor) return;
    const camera = this.viewer.scene.getActiveCamera();
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const v = this.worldAnchor.clone().project(camera);
    const canvasX = (v.x + 1) * 0.5 * size.width;
    const canvasY = (-v.y + 1) * 0.5 * size.height;
    this.container.style.left = `${canvasX}px`;
    this.container.style.top = `${canvasY}px`;

    // Keep the rendered content inside the render frame. The container is a
    // 0×0 anchor and the children (button fan / input panel / hint bubble)
    // extend from it with their own transforms, so a world anchor near the
    // frame edge would push them out of view — the thickness/distance input
    // then clips off screen. Measure the children's union rect at the
    // anchor position and shift by the overflow; when the content is larger
    // than the frame, the top/left edge wins (the input lives there).
    const margin = RadialMenu.EDGE_MARGIN_PX;
    const area = this.viewer.renderArea.getBoundingClientRect();
    let minL = Infinity;
    let minT = Infinity;
    let maxR = -Infinity;
    let maxB = -Infinity;
    for (const child of Array.from(this.container.children)) {
      const r = child.getBoundingClientRect();
      if (r.width <= 0 && r.height <= 0) continue;
      minL = Math.min(minL, r.left);
      minT = Math.min(minT, r.top);
      maxR = Math.max(maxR, r.right);
      maxB = Math.max(maxB, r.bottom);
    }
    if (!isFinite(minL)) return;
    let dx = 0;
    let dy = 0;
    if (maxR > area.right - margin) dx = area.right - margin - maxR;
    if (minL + dx < area.left + margin) dx = area.left + margin - minL;
    if (maxB > area.bottom - margin) dy = area.bottom - margin - maxB;
    if (minT + dy < area.top + margin) dy = area.top + margin - minT;
    if (dx !== 0 || dy !== 0) {
      this.container.style.left = `${canvasX + dx}px`;
      this.container.style.top = `${canvasY + dy}px`;
    }
  }

  private handleDocumentClick(e: MouseEvent): void {
    if (this.container.contains(e.target as Node)) return;
    // For hint mode, the menu is "passive" — the controller drives state via
    // canvas clicks, so outside-clicks must NOT close it (otherwise the very
    // click meant to pick a target would tear the menu down).
    if (this.content?.type === 'hint') return;

    // Input mode treats outside-click as cancel, and DOES absorb the gesture so
    // the click that dismissed the editor doesn't also act on the canvas behind
    // it (you're aborting a typed value, not trying to click through).
    if (this.content?.type === 'input') {
      this.content.onCancel();
      this.absorbDismissGesture(e);
      return;
    }

    // Items mode (the radial button menu): an outside click — including the
    // empty gaps BETWEEN the fanned buttons, which aren't pointer-interactive —
    // just dismisses the menu. It must NOT absorb the gesture: the user clicking
    // next to a button is almost always trying to act on whatever is under the
    // cursor (select a node, grab a handle), and swallowing the click made the
    // menu feel like it was eating input. Close + notify, then let the click
    // fall through to the tool.
    if (this.content?.type === 'items') {
      this.content.onClose?.();
      this.hide();
      return;
    }

    this.hide();
  }

  /**
   * Swallow the mouse gesture that dismissed the menu so the line tool behind
   * it doesn't also fire. We catch this on the capture-phase `mousedown`,
   * which runs before the canvas's own bubble-phase handlers — stopping
   * propagation here keeps it from reaching them. But a click is mousedown →
   * mouseup → click, and the tool acts on `click`, so we also install one-shot
   * capture-phase suppressors for the trailing mouseup + click from the same
   * gesture, removing them once consumed (with a microtask fallback in case
   * the gesture never completes, so they can't eat a later unrelated click).
   */
  private absorbDismissGesture(e: MouseEvent): void {
    e.stopPropagation();
    e.stopImmediatePropagation();

    const remaining = new Set(['mouseup', 'click']);
    const suppress = (ev: Event): void => {
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      remaining.delete(ev.type);
      if (remaining.size === 0) cleanup();
    };
    const cleanup = (): void => {
      document.removeEventListener('mouseup', suppress, true);
      document.removeEventListener('click', suppress, true);
    };
    document.addEventListener('mouseup', suppress, true);
    document.addEventListener('click', suppress, true);
    setTimeout(cleanup, 0);
  }

  private renderHint(content: RadialMenuHintContent): void {
    this.container.innerHTML = '';
    const panel = document.createElement('div');
    panel.style.cssText = `
      position: absolute;
      left: 0; top: 0;
      transform: translate(${RADIUS_PX}px, -${BUTTON_SIZE_PX / 2}px);
      padding: 6px 10px;
      background: rgba(20, 20, 20, 0.92);
      border: 1px solid rgba(120, 200, 255, 0.45);
      border-radius: 4px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      color: rgba(255, 255, 255, 0.92);
      pointer-events: none;
      font: 12px/1.2 sans-serif;
      white-space: nowrap;
    `;
    panel.textContent = content.message;
    this.container.appendChild(panel);
  }
}
